import { ComponentType, LazyExoticComponent, lazy } from 'react';

export type RouteKey =
  | 'Login'
  | 'SignUp'
  | 'GuestOnboarding'
  | 'OnboardingPage'
  | 'Home'
  | 'EventRoom'
  | 'Availability'
  | 'ActivityDetails'
  | 'Profile'
  | 'CreateEventPage'
  | 'Events'
  | 'JoinPage'
  | 'PlanSummary'
  | 'ForgotPassword'
  | 'Interests'
  | 'NotFound';

// We keep them as separate functions so we can call them imperatively
const loaders: Record<RouteKey, () => Promise<{ default: ComponentType<object> }>> = {
  CreateEventPage: () =>
    import('../features/create-event/CreateEventPage').then((m) => ({
      default: m.CreateEventPage,
    })),
  OnboardingPage: () =>
    import('../pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage })),
  Login: () => import('../pages/Login').then((m) => ({ default: m.Login })),
  SignUp: () => import('../pages/SignUp').then((m) => ({ default: m.SignUp })),
  GuestOnboarding: () =>
    import('../pages/GuestOnboarding').then((m) => ({ default: m.GuestOnboarding })),
  Home: () => import('../pages/Home'),
  EventRoom: () => import('../pages/EventRoom'),
  Availability: () => import('../pages/Availability'),
  Interests: () => import('../pages/Interests'),
  ActivityDetails: () => import('../pages/ActivityDetails'),
  Profile: () => import('../pages/Profile'),
  Events: () => import('../pages/Events'),
  JoinPage: () => import('../pages/JoinPage'),
  PlanSummary: () => import('../pages/PlanSummary'),
  ForgotPassword: () =>
    import('../pages/ForgotPassword').then((m) => ({ default: m.ForgotPassword })),
  NotFound: () => import('../pages/NotFound').then((m) => ({ default: m.NotFound })),
};

// Export the lazy components for React Router
export const LazyRoutes: Record<RouteKey, LazyExoticComponent<ComponentType<object>>> = {
  CreateEventPage: lazy(loaders.CreateEventPage),
  OnboardingPage: lazy(loaders.OnboardingPage),
  Login: lazy(loaders.Login),
  SignUp: lazy(loaders.SignUp),
  GuestOnboarding: lazy(loaders.GuestOnboarding),
  Home: lazy(loaders.Home),
  EventRoom: lazy(loaders.EventRoom),
  Availability: lazy(loaders.Availability),
  Interests: lazy(loaders.Interests),
  ActivityDetails: lazy(loaders.ActivityDetails),
  Profile: lazy(loaders.Profile),
  Events: lazy(loaders.Events),
  JoinPage: lazy(loaders.JoinPage),
  PlanSummary: lazy(loaders.PlanSummary),
  ForgotPassword: lazy(loaders.ForgotPassword),
  NotFound: lazy(loaders.NotFound),
};

// Export the prefetch function
export const prefetchRoute = (key: RouteKey) => {
  const loader = loaders[key];
  if (loader) {
    loader(); // This triggers the network request
  }
};
